import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FaTimes } from 'react-icons/fa';
import ProductCard from './ProductCard';
import styled from 'styled-components';

const PRODUCTS_PER_PAGE = 20;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  border-radius: 8px;
  margin-bottom: 10px;

  @media (max-width: 1067px) {
    height: 250px;
    margin-bottom: -60px;
    margin-top: -90px;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.5s ease-in-out;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 5px 10px;
    height: 100%;
    width: 768px;
  }
`;

// ✅ Props থেকে addToCart, cartItems, toggleFavorite, isFavorite নেওয়া হচ্ছে
const FabrilifeProductList = ({
  addToCart: addToCartProp,
  cartItems: cartItemsProp = [],
  toggleFavorite: toggleFavoriteProp,
  isFavorite: isFavoriteProp,
}) => {
  const navigate = useNavigate();

  // ─── Products State ───
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ─── Filter State ───
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(200000);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // ─── Category State ───
  const [apiCategories, setApiCategories] = useState([]);
  const [apiMainCategories, setApiMainCategories] = useState([]);
  const [apiSubCategories, setApiSubCategories] = useState([]);
  const defaultCategories = ['Gadget', 'Toys', 'Home Accessories', 'PC Component', 'Gym', 'Smart Watch', 'Light'];

  // ─── Carousel State ───
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ════════════════════════════════
  //  API: Products Fetch
  // ════════════════════════════════
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = new URL('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/products');
        url.searchParams.append('page', currentPage);
        url.searchParams.append('per_page', PRODUCTS_PER_PAGE);

        const response = await fetch(url.toString(), { headers: getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        setProducts(data.products || []);
        setTotalPages(data.total_pages || 1);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('পণ্য লোড করা যায়নি।');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [currentPage, getAuthHeaders]);

  // ════════════════════════════════
  //  API: Categories Fetch
  // ════════════════════════════════
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://aman-life-style-bd-main-server-974485829900.asia-south1.run.app/api/categories');
        const data = await response.json();
        if (data.success && data.categories?.length > 0) {
          const active = data.categories.filter(cat => cat.isActive);
          setApiCategories(active.filter(c => c.level === 'category').map(c => c.name));
          setApiMainCategories(active.filter(c => c.level === 'main').map(c => c.name));
          setApiSubCategories(active.filter(c => c.level === 'subcategory').map(c => c.name));
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // ════════════════════════════════
  //  API: Carousel
  // ════════════════════════════════
  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await fetch('https://e-commerce-496984660515.asia-south1.run.app/active_images');
        const data = await response.json();
        if (data.status === 'success') setCarouselImages(data.images);
      } catch (err) {
        console.error('Carousel fetch error:', err);
      }
    };
    fetchCarouselImages();
  }, []);

  useEffect(() => {
    if (carouselImages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev === carouselImages.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // ════════════════════════════════
  //  ✅ Cart & Favorites — App.js এর props use করছে
  // ════════════════════════════════
  const addToCart = (product) => {
    if (addToCartProp) addToCartProp(product);
  };

  const toggleFavorite = (productId) => {
    if (toggleFavoriteProp) toggleFavoriteProp(productId);
  };

  const isFavorite = (productId) => {
    return isFavoriteProp ? isFavoriteProp(productId) : false;
  };

  // App.js থেকে আসা cartItems সরাসরি use করছে
  const cartItems = cartItemsProp;

  // ════════════════════════════════
  //  Filters
  // ════════════════════════════════
  const categories = apiCategories.length > 0 ? apiCategories : defaultCategories;

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setSelectedCategories(prev => checked ? [...prev, value] : prev.filter(c => c !== value));
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, 200000]);
    setMinPrice(0);
    setMaxPrice(200000);
    setSearchQuery('');
    setSortOption('');
    setSelectedMainCategory('');
    setSelectedSubCategory('');
  };

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = searchQuery === '' ||
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(product.category);
      const matchesMain = selectedMainCategory === '' || product.mainCategory === selectedMainCategory;
      const matchesSub = selectedSubCategory === '' || product.subCategory === selectedSubCategory;
      const matchesPrice = product.price >= minPrice && product.price <= maxPrice;
      return matchesSearch && matchesCategory && matchesMain && matchesSub && matchesPrice;
    });

    if (sortOption) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortOption) {
          case 'low_to_high': return a.price - b.price;
          case 'high_to_low': return b.price - a.price;
          case 'a_z': return a.name.localeCompare(b.name);
          case 'z_a': return b.name.localeCompare(a.name);
          case 'newest': return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          default: return 0;
        }
      });
    }
    return filtered;
  }, [products, selectedCategories, minPrice, maxPrice, searchQuery, sortOption, selectedMainCategory, selectedSubCategory]);

  // ════════════════════════════════
  //  Pagination
  // ════════════════════════════════
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  useEffect(() => {
    document.body.style.overflow = isMobileFilterOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileFilterOpen]);

  // ════════════════════════════════
  //  Filter UI
  // ════════════════════════════════
  const FilterContent = ({ isMobile = false }) => (
    <div className={`bg-white ${isMobile ? '' : 'border border-gray-300 rounded-lg'} overflow-hidden`}>
      <div className={`bg-[#F0F2F2] px-4 py-3 ${isMobile ? '' : 'border-b border-gray-300'} flex items-center justify-between`}>
        <h2 className="text-lg font-bold text-gray-900">Filters</h2>
        {isMobile && (
          <button onClick={() => setIsMobileFilterOpen(false)} className="text-gray-600 hover:text-gray-900 p-1">
            <FaTimes className="text-xl" />
          </button>
        )}
      </div>

      <div className={`p-4 space-y-6 ${isMobile ? 'max-h-[calc(100vh-180px)]' : 'max-h-[calc(100vh-120px)]'} overflow-y-auto custom-scrollbar`}>
        <div className="pb-4 border-b border-gray-300">
          <h3 className="text-base font-bold text-gray-900 mb-3">Search</h3>
          <input type="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm" />
        </div>

        <div className="pb-4 border-b border-gray-300">
          <h3 className="text-base font-bold text-gray-900 mb-3">Price Range</h3>
          <div className="flex gap-2 mb-3">
            <input type="number" value={minPrice} onChange={e => setMinPrice(Number(e.target.value))} placeholder="Min"
              className="w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] text-sm" />
            <input type="number" value={maxPrice} onChange={e => setMaxPrice(Number(e.target.value))} placeholder="Max"
              className="w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] text-sm" />
          </div>
          <input type="range" min="0" max="200000" value={priceRange[1]}
            onChange={e => { const v = parseInt(e.target.value); setPriceRange([0, v]); setMaxPrice(v); }}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            style={{ background: `linear-gradient(to right, #FF9900 0%, #FF9900 ${(priceRange[1] / 200000) * 100}%, #E5E7EB ${(priceRange[1] / 200000) * 100}%, #E5E7EB 100%)` }} />
          <div className="mt-2 flex justify-between text-xs text-gray-600">
            <span>৳{minPrice.toLocaleString()}</span>
            <span>৳{maxPrice.toLocaleString()}</span>
          </div>
        </div>

        <div className="pb-4 border-b border-gray-300">
          <h3 className="text-base font-bold text-gray-900 mb-3">Main Category</h3>
          <select value={selectedMainCategory} onChange={e => setSelectedMainCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] text-sm bg-white cursor-pointer">
            <option value="">All main categories</option>
            {apiMainCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        <div className="pb-4 border-b border-gray-300">
          <h3 className="text-base font-bold text-gray-900 mb-3">Sub Category</h3>
          <select value={selectedSubCategory} onChange={e => setSelectedSubCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] text-sm bg-white cursor-pointer">
            <option value="">All sub categories</option>
            {apiSubCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        <div className="pb-4 border-b border-gray-300">
          <h3 className="text-base font-bold text-gray-900 mb-3">Filter by Tag</h3>
          <div className="space-y-2">
            {categories.map(category => (
              <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded transition-colors group">
                <input type="checkbox" value={category} checked={selectedCategories.includes(category)}
                  onChange={handleCategoryChange}
                  className="w-4 h-4 text-[#FF9900] bg-white border-gray-400 rounded focus:ring-[#FF9900] focus:ring-2 cursor-pointer" />
                <span className="text-sm text-gray-800 group-hover:text-[#C45500] transition-colors">{category}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <h3 className="text-base font-bold text-gray-900 mb-3">Sort</h3>
          <select value={sortOption} onChange={e => setSortOption(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] text-sm bg-white cursor-pointer">
            <option value="">Relevance</option>
            <option value="low_to_high">Price: Low → High</option>
            <option value="high_to_low">Price: High → Low</option>
            <option value="a_z">Name: A → Z</option>
            <option value="z_a">Name: Z → A</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      <div className={`p-4 border-t border-gray-300 bg-white ${isMobile ? 'sticky bottom-0' : ''}`}>
        <button onClick={() => isMobile && setIsMobileFilterOpen(false)}
          className="w-full bg-[#232F3E] hover:bg-[#131921] text-white font-semibold py-3 rounded-lg transition-colors duration-200 mb-2">
          Apply Filters
        </button>
        <button onClick={handleClearFilters}
          className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 rounded-lg border border-gray-300 transition-colors duration-200">
          Clear All
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════
  //  Render
  // ════════════════════════════════
  return (
    <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">

      <div className="md:hidden px-4 mb-4 mt-4">
        <button onClick={() => setIsMobileFilterOpen(true)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 flex items-center justify-center gap-2 text-gray-900 font-medium hover:bg-gray-50 transition-colors shadow-sm">
          <FontAwesomeIcon icon={faFilter} className="text-[#FF9900]" />
          <span>Filters</span>
          {(selectedCategories.length > 0 || priceRange[1] !== 200000) && (
            <span className="bg-[#FF9900] text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {selectedCategories.length + (priceRange[1] !== 200000 ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {isMobileFilterOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1001] md:hidden"
            onClick={() => setIsMobileFilterOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-[85%] max-w-[320px] bg-white z-[1002] md:hidden shadow-2xl">
            <FilterContent isMobile={true} />
          </div>
        </>
      )}

      <div className="flex flex-col md:flex-row gap-4 px-4 md:px-6">
        <aside className="hidden md:block w-full md:w-[260px] flex-shrink-0 md:sticky md:top-4 h-fit">
          <FilterContent />
        </aside>

        <main className="flex-1 min-w-0">
          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="w-10 h-10 rounded-full border-4 border-gray-200 animate-spin"
                style={{ borderTopColor: '#ff3f6c' }} />
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-10 text-red-500 font-medium">{error}</div>
          )}

          {!loading && !error && filteredProducts.length === 0 && (
            <div className="bg-white border border-gray-300 rounded-lg p-8 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Found</h3>
              <button onClick={handleClearFilters}
                className="bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold px-6 py-2 rounded-lg">
                Clear Filters
              </button>
            </div>
          )}

          {!loading && !error && filteredProducts.length > 0 && (
            <>
              <div className="mb-4 px-2">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{filteredProducts.length}</span> results
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4 pb-6">
                {filteredProducts.map((product, index) => (
                  <div key={`${product.id}-${index}`} className="relative">
                    {product.PCW === 0 && (
                      <div className="absolute top-0 right-0 bg-[#00A650] text-white text-xs font-bold px-2 py-1 rounded-bl-lg z-10 shadow-md">
                        Free Delivery
                      </div>
                    )}
                    <ProductCard
                      product={product}
                      addToCart={addToCart}
                      toggleFavorite={toggleFavorite}
                      isFavorite={isFavorite}
                      cartItems={cartItems}
                      navigate={navigate}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 mb-12 px-4">
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-2">
            {getPageNumbers().map((number, index) => (
              <React.Fragment key={index}>
                {number === '...' ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button onClick={() => handlePageChange(number)}
                    className={`min-w-[40px] h-10 px-3 border rounded-lg font-medium transition-all duration-200 ${number === currentPage
                        ? 'bg-[#FF9900] text-white border-[#FF9900]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#FF9900] hover:text-[#FF9900]'
                      }`}>
                    {number}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
            <span className="hidden sm:inline">Next</span>
            <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
          </button>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 50%;
          background: #FF9900; cursor: pointer;
          border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px; height: 16px; border-radius: 50%;
          background: #FF9900; cursor: pointer;
          border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default FabrilifeProductList;